<template>
	<div class="hdl-privacy-landing">
		<div class="hdl-auth-container mb-8">
			<div class="mb-5 flex flex-row justify-center items-center">
				<img src="/static/images/logo.svg" width="32" alt="logo" class="auth-medley-logo" />
			</div>
			<div v-if="loading">
				<div class="hdl-heading-secondary text-center mb-5">
					Loading...
				</div>
			</div>
			<div v-else-if="invalidRequestUuid">
				<div class="hdl-heading-secondary text-center mb-5">
					Invalid request
				</div>
				<div>
					This privacy request is invalid, or could not be found. Please initiate a new request from your account settings.
				</div>
			</div>
			<div v-else-if="expiredRequestUuid">
				<div class="hdl-heading-secondary text-center mb-5">
					Expired request
				</div>
				<div>
					This privacy request is expired. Please initiate a new request from your account settings.
				</div>
			</div>
			<div v-else-if="requestingDeletion">
				<div class="hdl-heading-secondary text-center mb-5">
					Confirm account deletion request
				</div>
				<div class="mb-5 description">
					<p class="mb-2">Once you confirm the request we'll begin deletion of your account <strong>{{ user.email }}</strong>.</p>
					<p class="mb-2">This cannot be undone. There will be no way to retrieve your data once it is deleted.</p>
					<p class="mb-2">It may exist in encrypted emergency backups for up to 30 days after deletion, but not recoverably so.</p>
					<ul class="mb-2">
						<li>This will include deletion of any workspaces this account owns and all surveys and responses within those workspaces.</li>
						<li>This will include all personal information and accounts in workspaces you are a member of.</li>
					</ul>
					<div>
						<div v-if="invalidPassword" class="text-hdl-red mb-1">
							Invalid password for this account.
						</div>
						<hdl-textbox
							placeholder="Enter your password"
							v-model="password"
							password
							v-if="requestRequested"
						/>
					</div>
				</div>
				<div class="mb-5 flex flex-col" v-if="requestRequested">
					<hdl-button
						label="Confirm deletion request"
						@click.prevent="confirmOrCancelRequest('confirm')"
						hdl-style="primary"
						class="mb-4"
						:disabled="password.trim().length === 0"
						:is-loading="loadingConfirmation"
					/>
					<hdl-button
						label="Cancel request"
						@click.prevent="confirmOrCancelRequest('cancel')"
						hdl-style="plain"
						:is-loading="loadingConfirmation"
					/>
				</div>
			</div>
			<div v-else-if="requestingDownload">
				<div class="hdl-heading-secondary text-center mb-5">
					Confirm account download request
				</div>
				<div class="mb-5">
					<p class="mb-2">Once you confirm the request we'll begin collecting all information related to your account <strong>{{ user.email }}</strong>.</p>
					<p>Within a few weeks you'll receive an e-mail with a link to download your information.</p>
				</div>
				<div class="mb-5 flex flex-col" v-if="requestRequested">
					<hdl-button
						label="Confirm download request"
						@click.prevent="confirmOrCancelRequest('confirm')"
						hdl-style="primary"
						class="mb-4"
						:is-loading="loadingConfirmation"
					/>
					<hdl-button
						label="Cancel request"
						@click.prevent="confirmOrCancelRequest('cancel')"
						hdl-style="plain"
						:is-loading="loadingConfirmation"
					/>
				</div>
			</div>

			<div v-if="errorConfirming" class="text-hdl-red">
				We ran into an error processing your request. Please try again shortly.
			</div>
			<div v-if="requestConfirmed" class="text-hdl-green">
				We've received your confirmation and will being processing your request soon.
			</div>
			<div v-if="requestCancelled" class="text-hdl-green">
				Your request has been cancelled.
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';

import { fetcher, FetchResult } from '@/common/fetcher';
import { PrivacyRequestStatus, PrivacyRequestType, RawUserType } from '@/common/interfaces';

@Component
export default class AccountPrivacyLanding extends Vue {
	privacyRequestUuid: string | null = null;
	invalidRequestUuid: boolean = false;
	expiredRequestUuid: boolean = false;
	loading: boolean = false;

	password: string = "";

	requestType: PrivacyRequestType | null = null;
	requestStatus: PrivacyRequestStatus | null = null;
	user: RawUserType | null = null;

	errorConfirming: boolean = false;
	invalidPassword: boolean = false;
	loadingConfirmation: boolean = false;

	mounted(): void {
		const privacyRequestUuid = this.$route.params.requestUuid as string | undefined;
		if (privacyRequestUuid) {
			this.privacyRequestUuid = privacyRequestUuid;
			this.fetchPrivacyInfo();
		} else {
			this.invalidRequestUuid = true;
		}
	}

	fetchPrivacyInfo(): void {
		this.loading = true;
		fetcher.Get(
			`/account/privacy/requests/${this.privacyRequestUuid}/`
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					this.requestType = result.json.data.type;
					this.requestStatus = result.json.data.status;
					this.user = result.json.data.user;
					this.invalidRequestUuid = false;
					this.expiredRequestUuid = false;
				} else if (result.response.ok && !result.json.success) {
					if (result.json.code === 'PR-01') {
						this.invalidRequestUuid = true;
						this.expiredRequestUuid = false;
					} else if (result.json.code === 'PR-02') {
						this.invalidRequestUuid = false;
						this.expiredRequestUuid = true;
					}
				}
				this.loading = false;
			}
		);
	}

	confirmOrCancelRequest(action: 'confirm' | 'cancel'): void {
		if (this.privacyRequestUuid === null) {
			return;
		}

		this.loadingConfirmation = true;

		fetcher.Post(
			`/account/privacy/requests/${this.privacyRequestUuid}/`,
			{
				action,
				password: this.password
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					this.requestStatus = result.json.data.status;
					this.errorConfirming = false;
					this.invalidPassword = false;
				} else if (result.response.ok && !result.json.success) {
					if (result.json.code === 'PR-04') {
						// Invalid password
						this.invalidPassword = true;
					} else {
						this.errorConfirming = true;
						this.invalidPassword = false;
					}
				}
				this.loadingConfirmation = false;
			}
		);
	}

	get requestRequested(): boolean {
		return this.requestStatus !== null && this.requestStatus === PrivacyRequestStatus.REQUESTED;
	}

	get requestConfirmed(): boolean {
		return this.requestStatus !== null && this.requestStatus === PrivacyRequestStatus.CONFIRMED;
	}

	get requestCancelled(): boolean {
		return this.requestStatus !== null && this.requestStatus === PrivacyRequestStatus.CANCELLED;
	}

	get requestingDownload(): boolean {
		return (
			this.privacyRequestUuid !== null
			&& this.requestType === PrivacyRequestType.DATA_DUMP
		);
	}

	get requestingDeletion(): boolean {
		return (
			this.privacyRequestUuid !== null
			&& this.requestType === PrivacyRequestType.DELETE_REQUEST
		);
	}
}
</script>

<style lang="scss">
.hdl-privacy-landing {
	.description {
		ul, ol {
			@apply mb-3;
			display: block;
			list-style: disc outside none;
			padding: 0 0 0 20px;

			li > p {
				margin-bottom: 0 !important;
			}
		}
		ol {
			list-style-type: decimal;
		}
		ul ul, ol ul {
			list-style-type: circle;
			list-style-position: inside;
			margin-left: 15px;
		}
		ol ol, ul ol {
			list-style-type: lower-latin;
			list-style-position: inside;
			margin-left: 15px;
		}

		li {
			display: list-item;
			list-style-position: outside;
		}
	}
}
</style>
